import "./style.scss";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import HighchartsReact from "highcharts-react-official";
import FullscreenModal from "../common/FullscreenModal/FullscreenModal";
import { useTranslation } from "react-i18next";
import { useMantineColorScheme } from "@mantine/core";
import { useAtom } from "jotai";
import {
  generateLabels,
  generateDailyLabels,
  formatValue,
  fontFamily,
  stepMs,
  leftIndex,
  rightIndex,
  nearestIndex,
} from "./chartHelpers";
import { formatProjectDateTime, formatProjectNumber } from "../../utils/helpers";
import { projectDetailsAtom } from "../../utils/atoms/projectDetailsAtom";

const highstockModule = require("highcharts/highstock");
const Highcharts = highstockModule.default || highstockModule;

if (typeof window !== "undefined") {
  Highcharts.setOptions({ time: { useUTC: false } });
}

const createDateAxisFormatter = (dailyMode = false) => function () {
  const span = (this.axis.max || 0) - (this.axis.min || 0);
  const day = 86400000;
  if (dailyMode) return moment(this.value).format("HH:mm");
  if (span <= 16 * day) return moment(this.value).format("D MMM YYYY H:mm");
  if (span <= 120 * day) return moment(this.value).format("D MMM YYYY");
  return moment(this.value).format("MMM YYYY");
};

const createYAxisFormatter = (formatValue) => function () {
  return formatValue(this.value);
};

const createTooltipFormatter = ({
  data1,
  data2,
  data1Raw,
  data2Raw,
  dark,
  effectiveYAxisName,
  formatValue,
  hiddenRef,
  legendLocaleLabels,
  name,
  nearestIndex,
  timesMs,
  tooltipLabels,
}) => function () {
  const hoveredX = Number.isFinite(this.x) ? this.x : this.points?.[0]?.x;
  const idx = nearestIndex(timesMs, hoveredX);
  if (idx < 0) return false;
  const dt = formatProjectDateTime(hoveredX);
  const lines = [];
  const pElectrical = this.points?.find((p) => p?.series?.options?.id === "electrical");
  const pThermal = this.points?.find((p) => p?.series?.options?.id === "thermal");
  if (data1Raw.length && !hiddenRef.current.has("electrical")) {
    const val = Number.isFinite(pElectrical?.y) ? pElectrical.y : data1[Math.min(idx, data1.length - 1)];
    if (Number.isFinite(val)) lines.push(`<span style="color:#42b4e6">&#9679;</span> ${tooltipLabels?.electrical || legendLocaleLabels.electricalLoad || "Electrical Load"}: <b>${formatValue(val)}</b> ${effectiveYAxisName}`);
  }
  if (data2Raw.length && !hiddenRef.current.has("thermal")) {
    const val = Number.isFinite(pThermal?.y) ? pThermal.y : data2[Math.min(idx, data2.length - 1)];
    if (Number.isFinite(val)) lines.push(`<span style="color:#3dcd58">&#9679;</span> ${tooltipLabels?.thermal || legendLocaleLabels.thermalLoad || "Thermal Load"}: <b>${formatValue(val)}</b> ${effectiveYAxisName}`);
  }
  if (!lines.length && data1Raw.length) {
    const val = data1[Math.min(idx, data1.length - 1)];
    if (Number.isFinite(val)) lines.push(`<span style="color:#42b4e6">&#9679;</span> ${tooltipLabels?.generic || name || "Value"}: <b>${formatValue(val)}</b> ${effectiveYAxisName}`);
  }
  return `<div style="font-family:${fontFamily};font-size:11px;line-height:1.7;color:${dark ? "#d5d7e0" : "#222"};background:${dark ? "#222" : "#fff"};border:1px solid ${dark ? "#555" : "#ccc"};border-radius:4px;padding:5px 8px;box-shadow:0 2px 8px rgba(0,0,0,.3);white-space:nowrap"><strong style="font-size:12px">${dt}</strong><br/>${lines.join("<br/>")}</div>`;
};

const TimeSeriesGraph = ({
  dataAssset,
  dataAssset2,
  name,
  yAxisName,
  chartStartDate,
  fullViewport = false,
  hideTypeToggle = false,
  showFullscreenButton = true,
  onFullscreen,
  embedHeight = 360,
  showMinimap: showMinimapProp = true,
  minimapGap = 18,
  deferUntilVisible = true,
  inModalInstance = false,
  interval,
  onStats,
  onUnitChange,
  startTime = "00:00",
  legendLabels: legendLabelsProp,
  tooltipLabels,
  dailyMode = false,
  isUnitsUnique = false,
  showEnergy = true,
}) => {
  const [projectDetails] = useAtom(projectDetailsAtom);
  const projectNumberFormat = projectDetails?.projectDTO?.numberFormat;
  const { t } = useTranslation();
  const { colorScheme } = useMantineColorScheme();

  const chartRef = useRef(null);
  const containerRef = useRef(null);
  const hiddenRef = useRef(new Set());
  const resizeObserverRef = useRef(null);
  const [hidden, setHidden] = useState(new Set());
  const [xDomain, setXDomain] = useState(null);
  const [chartType, setChartType] = useState("line");
  const [powerUnit, setPowerUnit] = useState(() => (yAxisName || "W"));
  const [fullscreenModal, setFullscreenModal] = useState(false);
  const [isVisible, setIsVisible] = useState(!deferUntilVisible);
  const [barData1, setBarData1] = useState(null);
  const [barData2, setBarData2] = useState(null);
  const [pvTransformEnabled, setPvTransformEnabled] = useState(false);

  const formatYAxisValue = (value) =>
    formatProjectNumber(value, {
      numberFormat: projectNumberFormat,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  useEffect(() => {
    hiddenRef.current = hidden;
  }, [hidden]);

  useEffect(() => {
    if (!yAxisName) return;
    setPowerUnit(yAxisName);
  }, [yAxisName]);

  useEffect(() => {
    if (!deferUntilVisible) return;
    const node = containerRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting || entry.intersectionRatio > 0) setIsVisible(true);
      });
    }, { threshold: 0.01 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [deferUntilVisible]);

  const dark = colorScheme === "dark";
  const bg = dark ? "#111111" : "#ffffff";
  const axisText = dark ? "#d5d7e0" : "#495057";
  const grid = dark ? "#2f2f2f" : "#e9ecef";
  const mask = dark ? "rgba(80,160,255,0.18)" : "rgba(0,123,255,0.13)";
  const outline = dark ? "#4da3ff" : "#007bff";

  const nativeUnit = yAxisName || "W";
  const unitFactor = useMemo(() => {
    if (nativeUnit === powerUnit) return 1;
    if (nativeUnit === "W" && powerUnit === "kW") return 1 / 1000;
    if (nativeUnit === "kW" && powerUnit === "W") return 1000;
    return 1;
  }, [nativeUnit, powerUnit]);

  const effectiveYAxisName = powerUnit || yAxisName || "";
  const data1Raw = useMemo(() => Array.isArray(dataAssset) ? dataAssset : [], [dataAssset]);
  const data2Raw = useMemo(() => Array.isArray(dataAssset2) ? dataAssset2 : [], [dataAssset2]);
  const isPvOnlyGraph = useMemo(() => {
    if (data2Raw.length) return false;
    const combinedLabel = `${name || ""} ${tooltipLabels?.generic || ""}`.toLowerCase();
    return combinedLabel.includes("pv") || combinedLabel.includes("solar");
  }, [data2Raw.length, name, tooltipLabels]);

  const effectiveStart = useMemo(() => {
    if (chartStartDate) return new Date(chartStartDate);
    const now = new Date();
    return new Date(now.getFullYear() - 1, 0, 1, 0, 0, 0, 0);
  }, [chartStartDate]);

  const labels = useMemo(() => {
    const generated = dailyMode
      ? generateDailyLabels(interval || 15, effectiveStart)
      : generateLabels(effectiveStart, interval || 15, startTime);
    return generated.labels || [];
  }, [dailyMode, interval, effectiveStart, startTime]);

  const timesMs = useMemo(() => labels.map((d) => d.getTime()), [labels]);
  const data1Base = useMemo(() => data1Raw.map((v) => (Number.isFinite(+v) ? +v * unitFactor : null)), [data1Raw, unitFactor]);
  const data1 = useMemo(() => {
    if (!isPvOnlyGraph || !pvTransformEnabled) return data1Base;
    return data1Base.map((v) => (Number.isFinite(v) ? Math.abs(v) : v));
  }, [data1Base, isPvOnlyGraph, pvTransformEnabled]);
  const data2 = useMemo(() => data2Raw.map((v) => (Number.isFinite(+v) ? +v * unitFactor : null)), [data2Raw, unitFactor]);

  useEffect(() => {
    if (isPvOnlyGraph) return;
    setPvTransformEnabled(false);
  }, [isPvOnlyGraph]);

  const updateXDomainFromExtremes = useCallback((min, max) => {
    if (!timesMs.length || !Number.isFinite(min) || !Number.isFinite(max)) {
      setXDomain(null);
      return;
    }

    const fullMin = timesMs[0];
    const fullMax = timesMs[timesMs.length - 1];
    const tolerance = Math.max(1, stepMs);
    const isFullRange = Math.abs(min - fullMin) <= tolerance && Math.abs(max - fullMax) <= tolerance;

    if (isFullRange) {
      setXDomain((prev) => (prev === null ? prev : null));
      return;
    }

    setXDomain((prev) => {
      if (prev && Math.abs(prev[0] - min) <= 1 && Math.abs(prev[1] - max) <= 1) return prev;
      return [min, max];
    });
  }, [timesMs]);

  useEffect(() => {
    if (chartType !== "bar") return;
    let cancelled = false;
    const aggregate = () => {
      const daily1 = {};
      const len1 = Math.min(timesMs.length, data1.length);
      for (let i = 0; i < len1; i++) {
        if (!Number.isFinite(data1[i])) continue;
        const d = new Date(timesMs[i]);
        const key = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
        if (!daily1[key]) daily1[key] = { sum: 0, n: 0 };
        daily1[key].sum += data1[i];
        daily1[key].n++;
      }
      const result1 = Object.entries(daily1).sort((a, b) => +a[0] - +b[0]).map(([k, v]) => [+k, v.sum / v.n]);
      
      const daily2 = {};
      const len2 = Math.min(timesMs.length, data2.length);
      for (let i = 0; i < len2; i++) {
        if (!Number.isFinite(data2[i])) continue;
        const d = new Date(timesMs[i]);
        const key = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
        if (!daily2[key]) daily2[key] = { sum: 0, n: 0 };
        daily2[key].sum += data2[i];
        daily2[key].n++;
      }
      const result2 = Object.entries(daily2).sort((a, b) => +a[0] - +b[0]).map(([k, v]) => [+k, v.sum / v.n]);
      
      if (!cancelled) {
        setBarData1(result1);
        setBarData2(result2);
      }
    };
    if (typeof requestIdleCallback !== "undefined") {
      const id = requestIdleCallback(aggregate, { timeout: 500 });
      return () => {
        cancelled = true;
        cancelIdleCallback(id);
      };
    }
    const timer = setTimeout(aggregate, 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [chartType, timesMs, data1, data2]);

  useEffect(() => {
    // Reset bar data when switching away from bar mode
    if (chartType !== "bar") {
      setBarData1(null);
      setBarData2(null);
    }
  }, [chartType]);

  const legendLocaleLabels = useMemo(() => ({
    electricalLoad: t("result.timeSeries.electrical"),
    thermalLoad: t("result.timeSeries.thermal"),
    ...legendLabelsProp,
  }), [legendLabelsProp, t]);

  const currentWindow = useMemo(() => {
    if (!timesMs.length) return { startIdx: 0, endIdx: -1 };
    if (!xDomain) return { startIdx: 0, endIdx: timesMs.length - 1 };
    const startIdx = Math.max(0, leftIndex(timesMs, xDomain[0]) - 1);
    const endIdx = Math.min(timesMs.length - 1, rightIndex(timesMs, xDomain[1]));
    return { startIdx, endIdx };
  }, [timesMs, xDomain]);

  const calcStats = useCallback((values) => {
    let min = Infinity;
    let max = -Infinity;
    let sum = 0;
    let count = 0;
    for (let i = currentWindow.startIdx; i <= currentWindow.endIdx && i < values.length; i++) {
      const num = values[i];
      if (!Number.isFinite(num)) continue;
      if (num < min) min = num;
      if (num > max) max = num;
      sum += num;
      count++;
    }
    if (!count) return { min: 0, max: 0, mean: 0, energy: 0 };
    const mean = sum / count;
    const intervalMinutes = interval != null && Number.isFinite(+interval) && +interval > 0 ? +interval : 15;
    const energy = sum * (intervalMinutes / 60);
    return { min, max, mean, energy };
  }, [currentWindow, interval]);

  const legendItems = useMemo(() => {
    const unitLabel = effectiveYAxisName || "";
    const formatLegendValue = (value) => formatProjectNumber(value, {
      numberFormat: projectNumberFormat,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const items = [];
    if (data1Raw.length) {
      const stats = calcStats(data1);
      items.push({
        key: "electrical",
        label: legendLocaleLabels.electricalLoad || name || "Electrical Load",
        color: "#42b4e6",
        visible: !hidden.has("electrical"),
        minText: formatLegendValue(stats.min),
        maxText: formatLegendValue(stats.max),
        avgText: formatLegendValue(stats.mean),
        energyText: formatLegendValue(stats.energy),
        unitLabel,
      });
    }
    if (data2Raw.length) {
      const stats = calcStats(data2);
      items.push({
        key: "thermal",
        label: legendLocaleLabels.thermalLoad || "Thermal Load",
        color: "#3dcd58",
        visible: !hidden.has("thermal"),
        minText: formatLegendValue(stats.min),
        maxText: formatLegendValue(stats.max),
        avgText: formatLegendValue(stats.mean),
        energyText: formatLegendValue(stats.energy),
        unitLabel,
      });
    }
    return items;
  }, [calcStats, data1, data1Raw.length, data2, data2Raw.length, effectiveYAxisName, hidden, legendLocaleLabels, name, projectNumberFormat]);

  // Find min/max points with timestamps for visual markers
  const minMaxPoints = useMemo(() => {
    const points = {
      electrical: { minPoint: null, maxPoint: null, minIdx: -1, maxIdx: -1 },
      thermal: { minPoint: null, maxPoint: null, minIdx: -1, maxIdx: -1 },
    };

    // Get visible range - for full year, use entire data array
    let startIdx = 0;
    let endIdx = Math.max(0, Math.min(data1.length, data2.length) - 1);
    
    if (xDomain && timesMs.length) {
      const [xMin, xMax] = xDomain;
      startIdx = Math.max(0, timesMs.findIndex((tMs) => tMs >= xMin));
      endIdx = timesMs.length - 1;
      for (let i = timesMs.length - 1; i >= 0; i--) {
        if (timesMs[i] <= xMax) {
          endIdx = i;
          break;
        }
      }
    }

    // Find electrical min/max
    let minVal = Infinity, maxVal = -Infinity;
    let minIdx = -1, maxIdx = -1;
    for (let i = startIdx; i <= endIdx && i < data1.length; i++) {
      const val = data1[i];
      if (Number.isFinite(val)) {
        if (val < minVal) { minVal = val; minIdx = i; }
        if (val > maxVal) { maxVal = val; maxIdx = i; }
      }
    }
    if (minIdx >= 0 && Number.isFinite(timesMs[minIdx])) {
      points.electrical.minPoint = [timesMs[minIdx], minVal];
      points.electrical.minIdx = minIdx;
    }
    if (maxIdx >= 0 && Number.isFinite(timesMs[maxIdx])) {
      points.electrical.maxPoint = [timesMs[maxIdx], maxVal];
      points.electrical.maxIdx = maxIdx;
    }

    // Find thermal min/max
    minVal = Infinity; maxVal = -Infinity;
    minIdx = -1; maxIdx = -1;
    for (let i = startIdx; i <= endIdx && i < data2.length; i++) {
      const val = data2[i];
      if (Number.isFinite(val)) {
        if (val < minVal) { minVal = val; minIdx = i; }
        if (val > maxVal) { maxVal = val; maxIdx = i; }
      }
    }
    if (minIdx >= 0 && Number.isFinite(timesMs[minIdx])) {
      points.thermal.minPoint = [timesMs[minIdx], minVal];
      points.thermal.minIdx = minIdx;
    }
    if (maxIdx >= 0 && Number.isFinite(timesMs[maxIdx])) {
      points.thermal.maxPoint = [timesMs[maxIdx], maxVal];
      points.thermal.maxIdx = maxIdx;
    }

    return points;
  }, [data1, data2, xDomain, timesMs]);

  // Calculate min/max for visible range (full year or zoomed range)
  const globalStats = useMemo(() => {
    const stats = {
      electrical: { min: Infinity, max: -Infinity },
      thermal: { min: Infinity, max: -Infinity },
    };

    // If xDomain is set (zoomed), calculate stats for visible range only
    if (xDomain && timesMs.length) {
      const [xMin, xMax] = xDomain;
      const startIdx = Math.max(0, timesMs.findIndex((tMs) => tMs >= xMin));
      let endIdx = timesMs.length - 1;
      for (let i = timesMs.length - 1; i >= 0; i--) {
        if (timesMs[i] <= xMax) {
          endIdx = i;
          break;
        }
      }

      for (let i = startIdx; i <= endIdx && i < data1.length; i++) {
        const val = data1[i];
        if (Number.isFinite(val)) {
          stats.electrical.min = Math.min(stats.electrical.min, val);
          stats.electrical.max = Math.max(stats.electrical.max, val);
        }
      }

      for (let i = startIdx; i <= endIdx && i < data2.length; i++) {
        const val = data2[i];
        if (Number.isFinite(val)) {
          stats.thermal.min = Math.min(stats.thermal.min, val);
          stats.thermal.max = Math.max(stats.thermal.max, val);
        }
      }
    } else {
      // Full year view - calculate for all points
      for (let i = 0; i < data1.length; i++) {
        const val = data1[i];
        if (Number.isFinite(val)) {
          stats.electrical.min = Math.min(stats.electrical.min, val);
          stats.electrical.max = Math.max(stats.electrical.max, val);
        }
      }

      for (let i = 0; i < data2.length; i++) {
        const val = data2[i];
        if (Number.isFinite(val)) {
          stats.thermal.min = Math.min(stats.thermal.min, val);
          stats.thermal.max = Math.max(stats.thermal.max, val);
        }
      }
    }

    if (stats.electrical.min === Infinity) stats.electrical.min = 0;
    if (stats.electrical.max === -Infinity) stats.electrical.max = 0;
    if (stats.thermal.min === Infinity) stats.thermal.min = 0;
    if (stats.thermal.max === -Infinity) stats.thermal.max = 0;

    return stats;
  }, [data1, data2, xDomain, timesMs]);

  useEffect(() => {
    if (typeof onStats !== "function") return;
    const electrical = data1Raw.length ? calcStats(data1) : null;
    const thermal = data2Raw.length ? calcStats(data2) : null;
    if (electrical && thermal) onStats({ electrical, thermal });
    else if (electrical) onStats({ electrical });
  }, [calcStats, data1, data1Raw.length, data2, data2Raw.length, onStats]);

  const seriesDefs = useMemo(() => {
    const fill = (color) => ({
      linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
      stops: [
        [0, Highcharts.color(color).setOpacity(0.28).get("rgba")],
        [1, Highcharts.color(color).setOpacity(0).get("rgba")],
      ],
    });

    // For full year view: downsample to 5 representative points per day
    // (first, min, mid, max, last) so min/max are always visible
    const downsampleFivePerDay = (timesArr, dataArr) => {
      const result = [];
      const len = Math.min(timesArr.length, dataArr.length);
      let i = 0;
      while (i < len) {
        const d = new Date(timesArr[i]);
        const dayKey = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
        let j = i;
        while (j < len) {
          const dj = new Date(timesArr[j]);
          if (new Date(dj.getFullYear(), dj.getMonth(), dj.getDate()).getTime() !== dayKey) break;
          j++;
        }
        const dayLen = j - i;
        if (dayLen > 0) {
          let minVal = Infinity, maxVal = -Infinity;
          let minIdx = i, maxIdx = i;
          for (let k = i; k < j; k++) {
            const v = dataArr[k];
            if (Number.isFinite(v)) {
              if (v < minVal) { minVal = v; minIdx = k; }
              if (v > maxVal) { maxVal = v; maxIdx = k; }
            }
          }
          const idxSet = new Set([i, minIdx, i + Math.floor(dayLen / 2), maxIdx, j - 1]);
          Array.from(idxSet).sort((a, b) => a - b).forEach((idx) => {
            result.push([timesArr[idx], dataArr[idx]]);
          });
        }
        i = j;
      }
      return result;
    };

    const useDownsampled = !dailyMode && !xDomain;

    const items = [];
    const len1 = Math.min(timesMs.length, data1.length);
    const len2 = Math.min(timesMs.length, data2.length);

    if (len1) {
      const lineData = useDownsampled
        ? downsampleFivePerDay(timesMs.slice(0, len1), data1.slice(0, len1))
        : Array.from({ length: len1 }, (_, i) => [timesMs[i], data1[i]]);
      items.push({
        id: "electrical",
        name: legendLocaleLabels.electricalLoad || name || "Electrical Load",
        type: "area",
        color: "#42b4e6",
        data: lineData,
        visible: !hidden.has("electrical"),
        showInNavigator: !!showMinimapProp,
        navigatorOptions: { color: "#42b4e6", lineWidth: 1 },
        fillColor: fill("#42b4e6"),
        threshold: 0,
        dataGrouping: useDownsampled
          ? { enabled: false }
          : { enabled: true, groupPixelWidth: 2, approximation: "high", smoothed: false },
        marker: { enabled: false },
      });
    }

    if (len2) {
      const lineData = useDownsampled
        ? downsampleFivePerDay(timesMs.slice(0, len2), data2.slice(0, len2))
        : Array.from({ length: len2 }, (_, i) => [timesMs[i], data2[i]]);
      items.push({
        id: "thermal",
        name: legendLocaleLabels.thermalLoad || "Thermal Load",
        type: "area",
        color: "#3dcd58",
        data: lineData,
        visible: !hidden.has("thermal"),
        showInNavigator: !!showMinimapProp,
        navigatorOptions: { color: "#3dcd58", lineWidth: 1 },
        fillColor: fill("#3dcd58"),
        threshold: 0,
        dataGrouping: useDownsampled
          ? { enabled: false }
          : { enabled: true, groupPixelWidth: 2, approximation: "high", smoothed: false },
        marker: { enabled: false },
      });
    }

    return items;
  }, [data1, data2, hidden, legendLocaleLabels, name, showMinimapProp, timesMs, data1Raw.length, data2Raw.length, xDomain]);

  // Bar-only series - use dataGrouping for aggregation
  const barSeriesDefs = useMemo(() => {
    const items = [];
    const len1 = Math.min(timesMs.length, data1.length);
    const len2 = Math.min(timesMs.length, data2.length);

    if (len1) {
      const barData = [];
      for (let i = 0; i < len1; i++) barData.push([timesMs[i], data1[i]]);
      items.push({
        id: "electrical",
        name: legendLocaleLabels.electricalLoad || name || "Electrical Load",
        type: "column",
        color: "#42b4e6",
        data: barData,
        visible: !hidden.has("electrical"),
        dataGrouping: { enabled: true, groupPixelWidth: 2, approximation: "high", smoothed: false },
        marker: { enabled: false },
      });
    }

    if (len2) {
      const barData = [];
      for (let i = 0; i < len2; i++) barData.push([timesMs[i], data2[i]]);
      items.push({
        id: "thermal",
        name: legendLocaleLabels.thermalLoad || "Thermal Load",
        type: "column",
        color: "#3dcd58",
        data: barData,
        visible: !hidden.has("thermal"),
        dataGrouping: { enabled: true, groupPixelWidth: 2, approximation: "high", smoothed: false },
        marker: { enabled: false },
      });
    }

    return items;
  }, [data1, data2, hidden, legendLocaleLabels, name, timesMs, data1Raw.length, data2Raw.length]);

  const chartHeight = fullViewport || inModalInstance
    ? (typeof window !== "undefined" ? window.innerHeight : embedHeight)
    : embedHeight;
  const showMinimap = showMinimapProp !== false && (fullViewport || inModalInstance || embedHeight >= 360);
  const currentXDomain = xDomain ? [xDomain[0], xDomain[1]] : null;

  // Separate minimal chart options for bar mode (vanilla chart, no stock features)
  const barChartOptions = useMemo(() => {
    if (chartType !== "bar") return null;
    return {
      chart: {
        backgroundColor: bg,
        height: chartHeight,
        animation: false,
        style: { fontFamily },
        panning: { enabled: true, type: "x" },
        zooming: { type: "x", mouseWheel: { enabled: false }, resetButton: { theme: { display: "none" } } },
      },
      credits: { enabled: false },
      title: { text: null },
      rangeSelector: { enabled: false },
      legend: { enabled: false },
      scrollbar: { enabled: false },
      navigator: showMinimap ? {
        enabled: true,
        height: 44,
        margin: 6,
        maskFill: mask,
        outlineColor: outline,
        handles: { backgroundColor: outline, borderColor: outline },
        xAxis: { labels: { enabled: false }, gridLineWidth: 0 },
        yAxis: { gridLineWidth: 0 },
        series: { type: "line", lineWidth: 1 },
      } : { enabled: false },
      xAxis: {
        type: "datetime",
        ordinal: false,
        lineColor: grid,
        tickColor: grid,
        crosshair: { color: axisText, dashStyle: "Dash", width: 1, snap: false, label: { enabled: false } },
        labels: {
          style: { color: axisText, fontSize: "11px" },
          formatter: createDateAxisFormatter(),
        },
        min: currentXDomain ? currentXDomain[0] : undefined,
        max: currentXDomain ? currentXDomain[1] : undefined,
        events: {
          afterSetExtremes(e) {
            updateXDomainFromExtremes(e.min, e.max);
          },
        },
      },
      yAxis: [{
        title: { text: effectiveYAxisName || null, style: { color: axisText, fontSize: "12px" } },
        opposite: false,
        gridLineColor: grid,
        labels: {
          style: { color: axisText, fontSize: "11px" },
          formatter: createYAxisFormatter(formatYAxisValue),
        },
      }],
      tooltip: {
        shared: true,
        split: false,
        outside: false,
        useHTML: true,
        valueDecimals: 2,
        borderRadius: 4,
        backgroundColor: dark ? "#222" : "#fff",
        borderColor: dark ? "#555" : "#666",
        style: { fontSize: "12px", color: dark ? "#d5d7e0" : "#222222" },
      },
      plotOptions: {
        series: {
          animation: false,
          states: {
            hover: { marker: { enabled: true, radius: 2.5 }, halo: { size: 0 }, lineWidthPlus: 0, brightness: 0 },
            inactive: { opacity: 1 },
          },
          turboThreshold: 0,
          stickyTracking: true,
          boostThreshold: Number.MAX_VALUE,
          dataGrouping: { enabled: true, groupPixelWidth: 2, approximation: "high", smoothed: false },
          marker: { enabled: false },
        },
        column: {
          borderWidth: 0,
          pointPadding: 0.05,
          groupPadding: 0.1,
          minPointLength: 2,
        },
      },
      series: barSeriesDefs,
    };
  }, [axisText, bg, chartHeight, chartType, currentXDomain, dark, effectiveYAxisName, grid, barSeriesDefs, mask, showMinimap, outline, globalStats, data1Raw.length, projectNumberFormat, updateXDomainFromExtremes]);

  const chartOptions = useMemo(() => ({
    chart: {
      backgroundColor: bg,
      height: chartHeight,
      animation: false,
      style: { fontFamily },
      panning: { enabled: true, type: "x" },
      zooming: { type: "x", mouseWheel: { enabled: false }, resetButton: { theme: { display: "none" } } },
    },
    credits: { enabled: false },
    title: { text: null },
    rangeSelector: { enabled: false },
    scrollbar: { enabled: false },
    legend: { enabled: false },
    navigator: showMinimap && chartType !== "bar" ? {
      enabled: true,
      height: 44,
      margin: 6,
      maskFill: mask,
      outlineColor: outline,
      handles: { backgroundColor: outline, borderColor: outline },
      xAxis: { labels: { enabled: false }, gridLineWidth: 0 },
      yAxis: { gridLineWidth: 0 },
      series: { type: "line", lineWidth: 1 },
    } : { enabled: false },
    xAxis: {
      type: "datetime",
      ordinal: false,
      lineColor: grid,
      tickColor: grid,
      crosshair: { color: axisText, dashStyle: "Dash", width: 1, snap: false, label: { enabled: false } },
      labels: {
        style: { color: axisText, fontSize: "11px" },
        formatter: createDateAxisFormatter(dailyMode),
      },
      min: (chartType === "bar" || !currentXDomain) ? undefined : currentXDomain[0],
      max: (chartType === "bar" || !currentXDomain) ? undefined : currentXDomain[1],
      events: {
        afterSetExtremes(e) {
          updateXDomainFromExtremes(e.min, e.max);
        },
      },
    },
    yAxis: [{
      title: { text: effectiveYAxisName || null, style: { color: axisText, fontSize: "12px" } },
      opposite: false,
      gridLineColor: grid,
      labels: {
        style: { color: axisText, fontSize: "11px" },
        formatter: createYAxisFormatter(formatYAxisValue),
      },
    }],
    tooltip: {
      shared: true,
      split: false,
      outside: false,
      useHTML: true,
      valueDecimals: 2,
      borderRadius: 4,
      backgroundColor: dark ? "#222" : "#fff",
      borderColor: dark ? "#555" : "#666",
      style: { fontSize: "12px", color: dark ? "#d5d7e0" : "#222222" },
      formatter: createTooltipFormatter({
        data1,
        data2,
        data1Raw,
        data2Raw,
        dark,
        effectiveYAxisName,
        formatValue,
        hiddenRef,
        legendLocaleLabels,
        name,
        nearestIndex,
        timesMs,
        tooltipLabels,
      }),
    },
    plotOptions: {
      series: {
        animation: false,
        states: {
          hover: { marker: { enabled: true, radius: 2.5 }, halo: { size: 0 }, lineWidthPlus: 0, brightness: 0 },
          inactive: { opacity: 1 },
        },
        turboThreshold: 0,
        stickyTracking: true,
        boostThreshold: Number.MAX_VALUE,
        dataGrouping: { enabled: true, groupPixelWidth: 2, approximation: "high", smoothed: false },
        marker: { enabled: false },
      },
      column: {
        borderWidth: 0,
        pointPadding: 0.05,
        groupPadding: 0.1,
        minPointLength: 2,
      },
    },
    series: seriesDefs,
  }), [axisText, bg, chartHeight, dark, dailyMode, effectiveYAxisName, grid, legendLocaleLabels, mask, name, outline, projectNumberFormat, seriesDefs, showMinimap, timesMs, tooltipLabels, data1, data2, data1Raw.length, data2Raw.length, xDomain, globalStats, updateXDomainFromExtremes]);

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart?.series) return;
    chart.series.forEach((series) => {
      const id = series.options?.id;
      if (!id) return;
      const shouldBeVisible = !hidden.has(id);
      if (series.visible !== shouldBeVisible) series.setVisible(shouldBeVisible, false);
    });
    chart.redraw(false);
  }, [hidden, seriesDefs]);

  useEffect(() => {
    const chart = chartRef.current?.chart;
    const node = containerRef.current;
    if (!chart || !node || typeof ResizeObserver === "undefined") return;
    resizeObserverRef.current?.disconnect?.();
    const observer = new ResizeObserver(() => chart.reflow());
    observer.observe(node);
    resizeObserverRef.current = observer;
    return () => observer.disconnect();
  }, [chartHeight, embedHeight, fullViewport, isVisible]);

  const getChart = () => chartRef.current?.chart;

  const handleAutoFitYAxis = useCallback(() => {
    const chart = getChart();
    if (!chart?.xAxis?.[0] || !chart?.yAxis?.[0] || !timesMs.length) return;
    const xAxis = chart.xAxis[0];
    const yAxis = chart.yAxis[0];
    const xMin = xAxis.min ?? timesMs[0];
    const xMax = xAxis.max ?? timesMs[timesMs.length - 1];
    const startIdx = Math.max(0, timesMs.findIndex((tMs) => tMs >= xMin));
    let endIdx = timesMs.length - 1;
    for (let i = timesMs.length - 1; i >= 0; i--) {
      if (timesMs[i] <= xMax) {
        endIdx = i;
        break;
      }
    }
    if (startIdx > endIdx) return;

    let yMin = Infinity;
    let yMax = -Infinity;
    for (let i = startIdx; i <= endIdx; i++) {
      if (data1.length && Number.isFinite(data1[i])) {
        yMin = Math.min(yMin, data1[i]);
        yMax = Math.max(yMax, data1[i]);
      }
      if (data2.length && Number.isFinite(data2[i])) {
        yMin = Math.min(yMin, data2[i]);
        yMax = Math.max(yMax, data2[i]);
      }
    }

    if (yMin === Infinity || yMax === -Infinity) return;
    const padding = (yMax - yMin) * 0.05;
    yAxis.setExtremes(Math.max(0, yMin - padding), yMax + padding, true, false);
  }, [data1, data2, timesMs]);

  const handleZoomIn = () => {
    const chart = getChart();
    if (!chart?.xAxis?.[0] || !timesMs.length) return;
    const axis = chart.xAxis[0];
    const min = axis.min ?? timesMs[0];
    const max = axis.max ?? timesMs[timesMs.length - 1];
    const span = Math.max(stepMs * 4, (max - min) * 0.5);
    const mid = (min + max) / 2;
    const nextMin = Math.max(timesMs[0], mid - span / 2);
    const nextMax = Math.min(timesMs[timesMs.length - 1], mid + span / 2);
    axis.setExtremes(nextMin, nextMax, true, false);
    setTimeout(() => handleAutoFitYAxis(), 80);
  };

  const handleZoomOut = () => {
    const chart = getChart();
    if (!chart?.xAxis?.[0] || !timesMs.length) return;
    const axis = chart.xAxis[0];
    const fullMin = timesMs[0];
    const fullMax = timesMs[timesMs.length - 1];
    const min = axis.min ?? fullMin;
    const max = axis.max ?? fullMax;
    const nextSpan = (max - min) / 0.7;
    if (nextSpan >= (fullMax - fullMin)) {
      axis.setExtremes(null, null, true, false);
      return;
    }
    const mid = (min + max) / 2;
    axis.setExtremes(
      Math.max(fullMin, mid - nextSpan / 2),
      Math.min(fullMax, mid + nextSpan / 2),
      true,
      false,
    );
  };

  const handleResetZoom = () => {
    const chart = getChart();
    if (!chart?.xAxis?.[0] || !chart?.yAxis?.[0] || !timesMs.length) return;
    chart.xAxis[0].setExtremes(timesMs[0], timesMs[timesMs.length - 1], true, false);
    chart.yAxis[0].setExtremes(null, null, true, false);
    setXDomain(null);
  };

  const handleDownloadPng = () => {
    const chart = getChart();
    if (!chart) return;
    const svgEl = chart.container?.querySelector("svg");
    if (!svgEl) return;
    const cloned = svgEl.cloneNode(true);
    const bgRect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    bgRect.setAttribute("x", "0");
    bgRect.setAttribute("y", "0");
    bgRect.setAttribute("width", svgEl.getAttribute("width") || chart.chartWidth || 1200);
    bgRect.setAttribute("height", svgEl.getAttribute("height") || chart.chartHeight || 480);
    bgRect.setAttribute("fill", dark ? "#111111" : "#ffffff");
    cloned.insertBefore(bgRect, cloned.firstChild);
    const svgString = new XMLSerializer().serializeToString(cloned);
    const canvas = document.createElement("canvas");
    canvas.width = chart.chartWidth || 1200;
    canvas.height = chart.chartHeight || 480;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = dark ? "#111111" : "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `result-series-${new Date().toISOString().replace(/[:.]/g, "-")}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      });
    };
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;
  };

  const handleDownloadCsv = () => {
    if (!timesMs.length) return;
    const rows = [];
    const csvEscape = (val) => {
      const s = String(val ?? "");
      return /[,"\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = ["DateTime"];
    if (data1Raw.length) headers.push(`${legendLocaleLabels.electricalLoad || name || "Electrical Load"} (${effectiveYAxisName})`);
    if (data2Raw.length) headers.push(`${legendLocaleLabels.thermalLoad || "Thermal Load"} (${effectiveYAxisName})`);
    rows.push(headers.map(csvEscape).join(","));
    for (let i = 0; i < timesMs.length; i++) {
      const cols = [moment(timesMs[i]).format("YYYY-MM-DD HH:mm:ss")];
      if (data1Raw.length) cols.push(Number.isFinite(data1[i]) ? Number(data1[i]).toFixed(2) : "");
      if (data2Raw.length) cols.push(Number.isFinite(data2[i]) ? Number(data2[i]).toFixed(2) : "");
      rows.push(cols.map(csvEscape).join(","));
    }
    const blob = new Blob(["\uFEFF" + rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `result-series-${new Date().toISOString().replace(/[:.]/g, "-")}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const toggleSeries = useCallback((key) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        const currentlyVisible = [
          data1Raw.length > 0 && !prev.has("electrical"),
          data2Raw.length > 0 && !prev.has("thermal"),
        ].filter(Boolean).length;
        if (currentlyVisible <= 1) return prev;
        next.add(key);
      }
      return next;
    });
  }, [data1Raw.length, data2Raw.length]);

  const handleCloseModal = useCallback(() => {
    setFullscreenModal(false);
    if (typeof onFullscreen === "function") onFullscreen();
  }, [onFullscreen]);

  const hasData = data1Raw.length || data2Raw.length;

  return (
    <div className={`rs-wrapper ${dark ? "dark" : "light"}`} ref={containerRef} style={{ position: "relative" }}>
      <div className="rs-toolbar">
        {showFullscreenButton && (
          <button
            className="rs-btn"
            aria-label={inModalInstance ? "time-series-close-fullscreen" : "time-series-open-fullscreen"}
            title={inModalInstance ? t("common.closeFullscreen") : t("common.openFullscreen")}
            onClick={() => (inModalInstance ? handleCloseModal() : setFullscreenModal(true))}
          >
            <span role="img" aria-hidden="true" className="rs-pan-icon">{inModalInstance ? "🗗" : "⛶"}</span>
          </button>
        )}
        <button
          className={`rs-btn rs-btn-pan `}
          aria-label={chartType === "bar" ? "time-series-line" : "time-series-bars"}
          title={chartType === "bar" ? t("timeSeries.toolbar.lines") : t("timeSeries.toolbar.bars")}
          // aria-pressed={chartType === "bar"}
          onClick={() => {
            setChartType((prev) => {
              if (prev !== "bar") setXDomain(null);
              return prev === "line" ? "bar" : "line";
            });
          }}
          disabled={hideTypeToggle}
          style={hideTypeToggle ? { opacity: 0.5, pointerEvents: "none" } : undefined}
        >
          <span role="img" aria-hidden="true" className="rs-pan-icon">{chartType === "bar" ? "Bars" : "Lines"}</span>
        </button>
        <button className="rs-btn" aria-label="time-series-zoom-in" title={t("common.zoomIn")} onClick={handleZoomIn}>+</button>
        <button className="rs-btn" aria-label="time-series-zoom-out" title={t("common.zoomOut")} onClick={handleZoomOut}>-</button>
        <button className="rs-btn" aria-label="time-series-reset-zoom" title={t("common.resetZoom")} onClick={handleResetZoom}>Reset</button>
        {isPvOnlyGraph && (
          <button
            className="rs-btn"
            aria-label="time-series-transform-pv"
            title={pvTransformEnabled ? "Show signed PV values" : "Transform PV to positive"}
            aria-pressed={pvTransformEnabled}
            onClick={() => setPvTransformEnabled((prev) => !prev)}
          >
            {pvTransformEnabled ? "±" : "⇅"}
          </button>
        )}
        {!isUnitsUnique && (
          <select
            className="rs-btn rs-download-select"
            value={powerUnit}
            title={t("timeSeries.unitToggle")}
            onChange={(e) => {
              setPowerUnit(e.target.value);
              if (typeof onUnitChange === "function") onUnitChange(e.target.value);
            }}
            style={{ width: 90 }}
          >
            <option value="W">W</option>
            <option value="kW">kW</option>
          </select>
        )}
        {/* <button className="rs-btn" title={t("common.download") || "Download PNG"} onClick={handleDownloadPng}>PNG</button> */}
        <select
          className="rs-btn rs-download-select"
          defaultValue=""
          title={t("common.download") || "Download"}
          onChange={(e) => {
            const v = e.target.value;
            e.target.value = "";
            if (v === "png") handleDownloadPng();
            if (v === "csv") handleDownloadCsv();
          }}
          style={{ width: 110 }}
        >
          <option value="" disabled hidden>{t("common.download") || "Download"}</option>
          <option value="png">PNG</option>
          <option value="csv">CSV</option>
        </select>
      </div>

      <div className="rs-legend" style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        {legendItems.map((item) => (
          <button
            key={item.key}
            type="button"
            style={{
              margin: "2px 10px",
              padding: "4px 10px",
              opacity: item.visible ? "1" : "0.45",
              background: item.visible ? (dark ? "#2e2e2e" : "#ffffff") : (dark ? "#1e1e1e" : "#f1f3f5"),
            }}
            title={item.visible ? `Hide ${item.label}` : `Show ${item.label}`}
            onClick={() => toggleSeries(item.key)}
          >
            <span style={{ width: 14, height: 8, borderRadius: 2, background: item.color, display: "inline-block", marginRight: 6 }} />
            <span style={{ fontSize: 11, color: dark ? "#d5d7e0" : "#222" }}>
              {`${item.label}: Min ${item.minText} ${item.unitLabel} , Max ${item.maxText} ${item.unitLabel} , Avg ${item.avgText} ${item.unitLabel}${showEnergy ? ` , Energy ${item.energyText} ${item.unitLabel}h` : ""}`}
            </span>
          </button>
        ))}
      </div>

      {hasData ? (
        <div style={{ width: "100%", minHeight: chartHeight }}>
          {isVisible && chartType === "bar" && barChartOptions && (
            <HighchartsReact
              key="ts-bar"
              highcharts={Highcharts}
              constructorType="stockChart"
              options={barChartOptions}
              ref={chartRef}
            />
          )}
          {isVisible && chartType !== "bar" && (
            <HighchartsReact
              key="ts-line"
              highcharts={Highcharts}
              constructorType="stockChart"
              options={chartOptions}
              ref={chartRef}
            />
          )}
        </div>
      ) : (
        <div className="rs-chart" style={{ minHeight: 120, padding: "12px 16px", color: axisText }}>
          {t("common.noDataAvailable")}
        </div>
      )}

      <FullscreenModal
        open={fullscreenModal && !inModalInstance}
        onClose={handleCloseModal}
        title={t("result.timeSeries.title")}
      >
        <TimeSeriesGraph
          dataAssset={dataAssset}
          dataAssset2={dataAssset2}
          name={name}
          yAxisName={yAxisName}
          chartStartDate={chartStartDate}
          fullViewport
          hideTypeToggle={hideTypeToggle}
          showFullscreenButton={showFullscreenButton}
          onFullscreen={handleCloseModal}
          embedHeight={embedHeight}
          showMinimap={showMinimapProp}
          minimapGap={minimapGap}
          deferUntilVisible={false}
          inModalInstance
          interval={interval}
          onStats={onStats}
          onUnitChange={onUnitChange}
          startTime={startTime}
          legendLabels={legendLabelsProp}
          tooltipLabels={tooltipLabels}
          dailyMode={dailyMode}
          isUnitsUnique={isUnitsUnique}
        />
      </FullscreenModal>
    </div>
  );
};

export default TimeSeriesGraph;